import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { api, fmtEUR, fmtXOF } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { sizeLabel } from "../lib/sizeLabel";

const OPERATORS = [
  { key: "wave", label: "Wave" },
  { key: "orange_money", label: "Orange Money" },
  { key: "mtn_momo", label: "MTN MoMo" },
  { key: "moov_money", label: "Moov Money" },
];

// La valeur stockée reste en français (logique de livraison backend) ; seule l'étiquette est traduite.
const COUNTRIES = [
  { v: "Côte d'Ivoire", labelKey: "checkout.countryCi" },
  { v: "France", labelKey: "checkout.countryFr" },
  { v: "États-Unis", labelKey: "checkout.countryUs" },
  { v: "Autre", labelKey: "checkout.countryOther" },
];

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{label}</label>
    <input
      data-testid={testid}
      required
      {...props}
      className="w-full border border-ligne bg-card2 px-4 py-3.5 text-sm text-ivoire placeholder:text-argile/50 transition-colors focus:border-or"
    />
  </div>
);

export default function Commande() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [country, setCountry] = useState("Côte d'Ivoire");
  const [payment, setPayment] = useState("mobile_money");
  const [operator, setOperator] = useState("wave");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const delivery = country === "Côte d'Ivoire" ? 0 : 25000;
  const total = subtotal + delivery;

  if (items.length === 0)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-3xl text-ivoire">{t("cart.empty")}</p>
        <Link data-testid="checkout-empty-back" to="/boutique" className="border border-or/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-or hover:bg-or hover:text-noir">
          {t("common.discoverShop")}
        </Link>
      </main>
    );

  const submit = async (e) => {
    e.preventDefault();
    if (payment === "mobile_money" && !phone.trim()) {
      toast.error(t("checkout.momoErr"));
      return;
    }
    setSubmitting(true);
    const form = new FormData(e.currentTarget);
    const customer = {
      full_name: form.get("full_name"),
      email: form.get("email"),
      phone: phone || form.get("phone"),
      country,
      city: form.get("city"),
      address: form.get("address"),
      note: form.get("note") || "",
    };
    const cartItems = items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity }));
    try {
      if (payment === "carte") {
        const res = await api.post("/payments/checkout", {
          items: cartItems, customer, origin_url: window.location.origin,
        });
        window.location.href = res.data.checkout_url;
        return;
      }
      const res = await api.post("/orders", {
        items: cartItems,
        customer,
        payment_method: payment,
        operator: payment === "mobile_money" ? operator : null,
      });
      clear();
      navigate(`/confirmation?ref=${res.data.ref}`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("checkout.orderErr"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="checkout-page" className="mx-auto max-w-[1500px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <Reveal>
        <p className="eyebrow">{t("checkout.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-4xl uppercase tracking-tight text-ivoire sm:text-5xl">{t("checkout.pre")} <span className="italic normal-case text-or">{t("checkout.it")}</span></h1>
      </Reveal>

      <form onSubmit={submit} className="mt-14 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-or">{t("checkout.s1")}</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field label={t("checkout.fullName")} testid="checkout-input-fullname" name="full_name" placeholder={t("checkout.phName")} />
              <Field label={t("checkout.email")} testid="checkout-input-email" name="email" type="email" placeholder={t("checkout.phEmail")} />
              <Field label={t("checkout.phone")} testid="checkout-input-phone" name="phone" type="tel" placeholder={t("checkout.phPhone")} />
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("checkout.country")}</label>
                <select
                  data-testid="checkout-select-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full border border-ligne bg-card2 px-4 py-3.5 text-sm text-ivoire focus:border-or"
                >
                  {COUNTRIES.map((c) => <option key={c.v} value={c.v}>{t(c.labelKey)}</option>)}
                </select>
              </div>
              <Field label={t("checkout.city")} testid="checkout-input-city" name="city" placeholder={t("checkout.phCity")} />
              <Field label={t("checkout.address")} testid="checkout-input-address" name="address" placeholder={t("checkout.phAddress")} />
            </div>
            <div className="mt-5">
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("checkout.note")}</label>
              <textarea data-testid="checkout-input-note" name="note" rows="3" className="w-full border border-ligne bg-card2 px-4 py-3.5 text-sm text-ivoire placeholder:text-argile/50 focus:border-or" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.3em] text-or">{t("checkout.s2")}</p>
            <div className="mt-6 space-y-4">
              <label
                data-testid="payment-method-mobile-money"
                className={`flex cursor-pointer items-start gap-4 border p-5 transition-all ${payment === "mobile_money" ? "border-or bg-elevated" : "border-ligne bg-card2 hover:border-or/50"}`}
              >
                <input type="radio" name="payment" checked={payment === "mobile_money"} onChange={() => setPayment("mobile_money")} className="mt-1 accent-[#F7F5F0]" />
                <span>
                  <span className="block text-sm text-ivoire">{t("checkout.momoTitle")} <span className="text-argile">{t("checkout.momoVia")}</span></span>
                  <span className="mt-1 block text-xs text-argile">{t("checkout.momoSub")}</span>
                </span>
              </label>

              {payment === "mobile_money" && (
                <div className="ms-1 border-s border-or/40 ps-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("checkout.operator")}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {OPERATORS.map((o) => (
                      <button
                        type="button"
                        key={o.key}
                        data-testid={`operator-select-${o.key}`}
                        onClick={() => setOperator(o.key)}
                        className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all ${operator === o.key ? "border-or bg-or text-noir" : "border-ligne text-argile hover:border-or hover:text-or"}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 max-w-xs">
                    <Field label={t("checkout.momoPhone")} testid="checkout-input-momo-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("checkout.phMomo")} />
                  </div>
                </div>
              )}

              <label
                data-testid="payment-method-carte"
                className={`flex cursor-pointer items-start gap-4 border p-5 transition-all ${payment === "carte" ? "border-or bg-elevated" : "border-ligne bg-card2 hover:border-or/50"}`}
              >
                <input type="radio" name="payment" checked={payment === "carte"} onChange={() => setPayment("carte")} className="mt-1 accent-[#F7F5F0]" />
                <span>
                  <span className="block text-sm text-ivoire">{t("checkout.cardTitle")} <span className="text-argile">{t("checkout.cardVia")}</span></span>
                  <span className="mt-1 block text-xs text-argile">{t("checkout.cardSub")}</span>
                </span>
              </label>

              <label
                data-testid="payment-method-livraison"
                className={`flex cursor-pointer items-start gap-4 border p-5 transition-all ${payment === "livraison" ? "border-or bg-elevated" : "border-ligne bg-card2 hover:border-or/50"}`}
              >
                <input type="radio" name="payment" checked={payment === "livraison"} onChange={() => setPayment("livraison")} className="mt-1 accent-[#F7F5F0]" />
                <span>
                  <span className="block text-sm text-ivoire">{t("checkout.codTitle")}</span>
                  <span className="mt-1 block text-xs text-argile">{t("checkout.codSub")}</span>
                </span>
              </label>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15} className="lg:sticky lg:top-28">
            <div className="border border-ligne bg-card2 p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-or">{t("checkout.recap")}</p>
              <ul className="mt-6 space-y-5" data-testid="checkout-recap-items">
                {items.map((i) => (
                  <li key={i.key} className="flex gap-4">
                    <img src={i.image} alt={i.name} className="h-20 w-16 object-cover" />
                    <div className="flex flex-1 flex-col">
                      <p className="font-serif text-sm text-ivoire">{i.name}</p>
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-argile">{t("common.sizeX", { size: sizeLabel(i.size, t), n: i.quantity })}</p>
                      <p className="mt-auto text-sm text-or">{fmtXOF(i.price_xof * i.quantity)}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8 space-y-3 border-t border-ligne pt-6 text-sm">
                <div className="flex justify-between text-argile"><span>{t("common.subtotal")}</span><span data-testid="checkout-subtotal">{fmtXOF(subtotal)}</span></div>
                <div className="flex justify-between text-argile"><span>{t("common.delivery")}</span><span data-testid="checkout-delivery">{delivery === 0 ? t("common.free") : fmtXOF(delivery)}</span></div>
                <div className="flex items-baseline justify-between border-t border-ligne pt-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ivoire">{t("common.total")}</span>
                  <div className="text-end" data-testid="checkout-total">
                    <p className="font-serif text-2xl text-or">{fmtXOF(total)}</p>
                    <p className="text-xs text-argile">{fmtEUR(total)}</p>
                  </div>
                </div>
              </div>
              <button
                data-testid="checkout-submit-button"
                type="submit"
                disabled={submitting}
                className="mt-8 w-full bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair disabled:opacity-50"
              >
                {submitting ? t("checkout.confirming") : t("checkout.submit")}
              </button>
              <p className="mt-5 flex items-center justify-center gap-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-argile/70">
                <ShieldCheck size={13} className="text-or" /> {t("checkout.secure")}
              </p>
            </div>
          </Reveal>
        </div>
      </form>
    </main>
  );
}
